import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";

import "./App.css";
import { GOOGLE_OAUTH_CLIENT_ID } from "./constant";
import { queryConfig } from "./helpers";
import { initAnalytics } from "./helpers/analytics";
import { AppProvider } from "./helpers/app-context";
import { useCrossTabIdentitySync } from "./helpers/hooks";
import Routes from "./pages/routes";
import { TutorialOverlay } from "./components/Tutorial/TutorialOverlay";
import { AreaSelectionModal } from "./components/Tutorial/AreaSelectionModal";
import { PwaRuntime } from "./pwa/PwaRuntime";

const queryClient = new QueryClient({
  defaultOptions: queryConfig.defaultOptions,
});

const CHUNK_RELOAD_KEY = "chunk_reload_once";
const CHUNK_RELOAD_WINDOW_MS = 60_000;

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error && error.name === "ChunkLoadError") return true;
  return /Loading chunk .* failed|Failed to fetch dynamically imported module/.test(String(error));
}

// Lazy imports cache the rejected promise, so "Try Again" can't recover from a stale chunk - reload once per minute instead.
function ErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const [showCard, setShowCard] = useState(false);

  // All impure reads live in the effect; reload at most once per window, else show the card.
  useEffect(() => {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (isChunkLoadError(error) && Date.now() - lastReload > CHUNK_RELOAD_WINDOW_MS) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      window.location.reload();
    } else {
      setShowCard(true);
    }
  }, [error]);

  if (!showCard) return null;

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-surface-secondary p-4">
      <div className="max-w-md w-full bg-surface-primary rounded-lg shadow-modal p-6 text-center border border-border-primary">
        <h1 className="text-title-two font-semibold text-danger mb-4">
          Something went wrong
        </h1>
        <p className="text-body text-content-secondary mb-6">
          We've been notified and are working to fix the issue.
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => window.location.href = "/"}
            className="d-btn d-btn-secondary"
          >
            Go Home
          </button>
          <button
            onClick={resetError}
            className="d-btn d-btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  // Reload if another tab switches the signed-in account (shared localStorage).
  useCrossTabIdentitySync();

  // Note: The original useEffect cleanup was clearing localStorage during React remounts
  // which caused issues with route protection. If "staySignedIn" is not true, the user
  // should be logged out only when their session expires (handled by API 401 errors),
  // not during component remounts or navigation.

  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
      showDialog={false}
    >
      <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <AppProvider>
            <div className="flex flex-col h-[100vh]">
              <Routes />
            </div>
            <PwaRuntime />
            {/* Tutorial components */}
            <TutorialOverlay />
            <AreaSelectionModal />
          </AppProvider>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
